import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
  ArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
} from "react-admin";
import { useParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

import { settings } from "app";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import sessionExamsHelper from "components/AcademicYears/Exams/helper";
import { ClassTypes } from "utils/constants";
import { validateEnd, validateStart } from "utils/validations";
import { studentRequests } from "api/studentApi";
import { administrationRequests } from "api/administrationApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

const EditForm = () => {
  const { id } = useParams();
  const { setValue } = useFormContext();
  const notifyError = useNotifyError();

  useEffect(() => {
    const fetchProfessors = async () => {
      try {
        const res = await dataProvider.customRequest(studentRequests.getCyclicScheduleDisciplineProfessors(id));
        setValue("professors", res.data.professors);
      } catch (error) {
        notifyError(error);
      }
    };
    fetchProfessors();
  }, [id, setValue, notifyError]);

  return (
    <ArrayInput
      label="Преподаватели"
      source="professors"
    >
      <SimpleFormIterator sx={settings.style.iteratorWithoutGap}>
        <FormDataConsumer>
          {({ scopedFormData }) => (
            <>
              <Grid
                container
                spacing={2}
              >
                {scopedFormData?.attestationsGenerated ? (
                  <Grid
                    item
                    xs={12}
                  >
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "warning.main",
                        gap: 0.5,
                      }}
                    >
                      <WarningIcon />
                      Вече има генерирани заверки за това упр/лекция
                    </Typography>
                  </Grid>
                ) : null}
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <StudyGroupField
                    labelStyle={{ fontSize: "11px" }}
                    label="Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
                  групи!"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ProfessorInput
                    source="employeeId"
                    label="Преподавател"
                    filters={{ filterByUser: true }}
                    fullWidth
                    validate={[required()]}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ReferenceInput
                    source="classTypeId"
                    reference={administrationRequests.getRoomClassTypesSelect()}
                    filter={{ excludeIds: [ClassTypes.Csc] }}
                  >
                    <SelectInput
                      optionText="name"
                      label="Тип занятия"
                      validate={[required()]}
                    />
                  </ReferenceInput>
                </Grid>{" "}
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  {" "}
                  <NumberInput
                    onWheel={e => e.target.blur()}
                    source="fromDay"
                    label="От ден"
                    validate={() =>
                      validateStart(
                        scopedFormData.fromDay,
                        scopedFormData.toDay,
                        required,
                        "Началният ден не може да бъде по-голям от крайният",
                      )
                    }
                  />
                </Grid>{" "}
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <NumberInput
                    onWheel={e => e.target.blur()}
                    source="toDay"
                    label="До ден"
                    validate={() =>
                      validateEnd(
                        scopedFormData.toDay,
                        scopedFormData.fromDay,
                        required,
                        "Крайният ден не може да бъде по-малък от началният",
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomTimeOnlyInput
                    source="startTime"
                    label="Начало"
                    required={true}
                    validate={() =>
                      sessionExamsHelper.validateTimeSlots({
                        startTime: scopedFormData.startTime,
                        endTime: scopedFormData.endTime,
                      })
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomTimeOnlyInput
                    source="endTime"
                    label="Край"
                    required={true}
                    validate={() =>
                      sessionExamsHelper.validateTimeSlots({
                        startTime: scopedFormData.startTime,
                        endTime: scopedFormData.endTime,
                      })
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <ReferenceInput
                    source="buildingId"
                    reference={administrationRequests.getBuildingsSelect()}
                    label="Сграда"
                  >
                    <AutocompleteInput
                      label="Сграда"
                      optionText="name"
                      optionValue="id"
                      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                    />
                  </ReferenceInput>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <ReferenceInput
                    source="roomId"
                    reference={administrationRequests.getRoomsSelect()}
                  >
                    <AutocompleteInput
                      label="Зала"
                      optionValue="id"
                      optionText={rec =>
                        `${rec.roomName} - ${rec.buildingName}${rec.capacityExam ? ` (${rec.capacityExam})` : ""}`
                      }
                      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>
              </Grid>
            </>
          )}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
};
export default EditForm;
